import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CloseWithBackground, SearchIconSmall, Spinner } from 'app/components/shared/icons';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';
import useOnClickOutside from 'hooks/click-outside';
import useBehavioral, { BEHAVIORALS } from 'hooks/useBehavioral';
import useDebounce from 'hooks/useDebounce';
import useIsMobile from 'hooks/useIsMobile';
import {
  contextSelector,
  currentSelectedPersonSelector,
  PEOPLE_CONTEXT,
  setCurrentSelectedPerson,
  setOpenMobileNav,
} from 'store/reducers/people/people-reducer';

import usePeopleSearch from '../../hooks/usePeopleSearch';
import {
  PeopleSideBarSearchClearIconContainer,
  PeopleSideBarSearchContainer,
  PeopleSideBarSearchContentContainer,
  PeopleSideBarSearchIconsContainer,
  PeopleSideBarSearchInput,
  PeopleSideBarSearchTitle,
  PeopleSideBarSearchWrapper,
  PeopleSidebarSearchContentItemCompany,
  PeopleSidebarSearchContentItemContainer,
  PeopleSidebarSearchContentItemName,
  PeopleSidebarSearchContentList,
  PeopleSidebarSearchContentLoadingContainer,
  PeopleSidebarSearchContentWrapper,
  PeopleSidebarSearchNoMatches,
} from './styled';

export default function PeopleSideBarSearch() {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const context = useSelector(contextSelector);
  const [searchValue, setSearchValue] = useState<string>('');
  const searchDebouncedValue = useDebounce(searchValue, 250);
  const searchInputContainer = useRef<HTMLDivElement>(null);
  const currentSelectedPerson = useSelector(currentSelectedPersonSelector);
  const searchResultClickedBehavioralFunction = useBehavioral(
    BEHAVIORALS.COLLECTIVE_DIRECTORY_SEARCH_RESULT_CLICKED
  );
  const teamingSearchResultClickedBehavioralFunction = useBehavioral(
    BEHAVIORALS.TEAMING_SEARCH_CLICKED
  );

  useOnClickOutside(searchInputContainer, () => {
    setSearchValue('');
  });

  const searchQuery = usePeopleSearch(
    searchDebouncedValue,
    context === PEOPLE_CONTEXT.TEAMING,
    [],
    {
      enabled: false,
    }
  );
  const { data, isLoading } = searchQuery;

  useEffect(() => {
    if (searchDebouncedValue && searchDebouncedValue.trim().length > 0) {
      searchQuery.refetch();
    }
  }, [searchDebouncedValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const showContent = useMemo(() => {
    return searchDebouncedValue.trim().length > 0 && currentSelectedPerson === null;
  }, [searchDebouncedValue, currentSelectedPerson]);

  const handlePersonClick = (person) => {
    if (context === PEOPLE_CONTEXT.COLLECTIVE_DIRECTORY) {
      searchResultClickedBehavioralFunction({ related_object_uuid: person.uuid });
    } else {
      teamingSearchResultClickedBehavioralFunction({ related_object_uuid: person.uuid });
    }
    dispatch(setCurrentSelectedPerson(person));
    dispatch(setOpenMobileNav(false));
    setSearchValue('');
  };

  const handleClearFilterClick = () => {
    dispatch(setCurrentSelectedPerson(null));
  };

  return (
    <PeopleSideBarSearchWrapper>
      <PeopleSideBarSearchTitle>Find someone in particular</PeopleSideBarSearchTitle>
      <PeopleSideBarSearchContentContainer ref={searchInputContainer}>
        <PeopleSideBarSearchContainer>
          {currentSelectedPerson === null ? (
            <PeopleSideBarSearchInput
              placeholder="Search by name"
              name="person-search"
              value={searchValue}
              onChange={handleInputChange}
            />
          ) : (
            <PeopleSidebarSearchContentItemName style={{ maxWidth: 'unset' }}>
              {currentSelectedPerson.name}
            </PeopleSidebarSearchContentItemName>
          )}
          <PeopleSideBarSearchIconsContainer>
            {currentSelectedPerson !== null ? (
              <PeopleSideBarSearchClearIconContainer onClick={handleClearFilterClick}>
                <CloseWithBackground
                  width={18}
                  height={18}
                  fill="var(--color-data-alumni-background)"
                  background="rgba(217, 217, 217, 0.50)"
                />
              </PeopleSideBarSearchClearIconContainer>
            ) : null}
            <SearchIconSmall width={20} height={20} fill={'rgba(23, 28, 51, 0.5)'} />
          </PeopleSideBarSearchIconsContainer>
        </PeopleSideBarSearchContainer>
        <AnimatePresence>
          {showContent ? (
            <motion.div
              style={{
                overflow: 'hidden',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                background: '#ffffff',
                borderRadius: '24px',
                maxHeight: isMobile ? '35svh' : '350px',
                zIndex: 4,
                scrollbarWidth: 'none',
              }}
              initial={{ height: 0 }}
              animate={{ height: 'auto', overflowY: 'auto' }}
              transition={{ duration: 0.2 }}
              exit={{ height: 0 }}
            >
              <PeopleSidebarSearchContentWrapper>
                {!isLoading ? (
                  <PeopleSidebarSearchContentList>
                    {data && data?.length > 0 ? (
                      data?.map((person) => (
                        <PeopleSidebarSearchContentItemContainer
                          key={person.uuid}
                          onClick={() => handlePersonClick(person)}
                        >
                          <PeopleSidebarSearchContentItemName
                            dangerouslySetInnerHTML={{
                              __html: highlightMatch(searchDebouncedValue, person.name).text,
                            }}
                          />
                          <PeopleSidebarSearchContentItemCompany>
                            {person.info.replace('Person from ', '')}
                          </PeopleSidebarSearchContentItemCompany>
                        </PeopleSidebarSearchContentItemContainer>
                      ))
                    ) : (
                      <PeopleSidebarSearchNoMatches>No matches</PeopleSidebarSearchNoMatches>
                    )}
                  </PeopleSidebarSearchContentList>
                ) : (
                  <PeopleSidebarSearchContentLoadingContainer>
                    <Spinner width={24} height={24} fill="var(--color-primary)" />
                  </PeopleSidebarSearchContentLoadingContainer>
                )}
              </PeopleSidebarSearchContentWrapper>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </PeopleSideBarSearchContentContainer>
    </PeopleSideBarSearchWrapper>
  );
}

export function highlightMatch(searchTerm, text) {
  const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(escapedTerm, 'gi');
  text = text.replace(regex, (m) => '<span class="highlighted">' + m + '</span>');
  return { text };
}
